// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — ELK Auto-Layout Utility (Capa 2A)
// Uses elkjs layered algorithm with orthogonal edge routing
// ═══════════════════════════════════════════════════════════════

import ELK from 'elkjs/lib/elk.bundled.js';
import type { Node, Edge } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import { NODE_SIZES } from './flowSchema';
import type { TipoNodoFlujograma, FlowNodeData } from '../../types/flowchart.types';

const elk = new ELK();

export type LayoutDirection = 'DOWN' | 'RIGHT' | 'UP' | 'LEFT';

/**
 * Auto-layout nodes and edges using the ELK layered algorithm.
 * Returns a new array of nodes with updated positions.
 */
export async function autoLayoutELK(
  nodes: Node<FlowNodeData>[],
  edges: Edge[],
  direction: LayoutDirection = 'RIGHT',
): Promise<Node<FlowNodeData>[]> {
  if (nodes.length === 0) return nodes;

  const elkNodes = nodes.map((node) => {
    const nodeType = (node.data as FlowNodeData)?.nodeType as TipoNodoFlujograma | undefined;
    const size = nodeType ? NODE_SIZES[nodeType] : { w: T.node.width, h: T.node.height };

    // For decision nodes (diamond), use bounding box of the rotated square
    const width = nodeType === 'DECISION' ? T.node.decisionSize + 16 : size.w;
    const height = nodeType === 'DECISION' ? T.node.decisionSize + 16 : size.h;

    return {
      id: node.id,
      width,
      height,
    };
  });

  const elkEdges = edges.map((edge) => ({
    id: edge.id,
    sources: [edge.source],
    targets: [edge.target],
  }));

  // A3 canvas bounds for clamping
  const A3_W = T.canvas.a3Width;
  const A3_H = T.canvas.a3Height;

  const graph = await elk.layout({
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': direction,
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.spacing.nodeNode': String(T.spacing.nodeGap),
      'elk.layered.spacing.nodeNodeBetweenLayers': String(T.spacing.laneGap),
      'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
      'org.eclipse.elk.padding': '[left=40, top=40, right=40, bottom=40]',
    },
    children: elkNodes,
    edges: elkEdges,
  });

  // Map ELK positions back to React Flow nodes, clamped inside A3
  const positionMap = new Map<string, { x: number; y: number }>();
  for (const child of graph.children ?? []) {
    const cw = child.width ?? T.node.width;
    const ch = child.height ?? T.node.height;
    const cx = Math.max(0, Math.min(child.x ?? 0, A3_W - cw));
    const cy = Math.max(0, Math.min(child.y ?? 0, A3_H - ch));
    positionMap.set(child.id, { x: cx, y: cy });
  }

  return nodes.map((node) => {
    const pos = positionMap.get(node.id);
    if (!pos) return node;
    return {
      ...node,
      position: pos,
    };
  });
}
